import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Divider,
  Typography,
  Chip,
  TextField,
  Switch,
  Dialog,
  DialogContent,
} from "@mui/material";
import { adminTheme } from "theme/adminTheme";
import SaveIcon from "@mui/icons-material/Save";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useNavigate } from "react-router-dom";
import AIProcessGenerator from "./AIProcessGenerator";
import { StepCreateRequest } from "api/main/data-contracts";

interface ProcessToolbarProps {
  processName: string;
  isActive: boolean;
  onSave: () => void;
  onExport: () => void;
  onImport: () => void;
  onAddStep: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFitView: () => void;
  onAutoLayout: () => void;
  isDirty: boolean;
  isValid: boolean;
  onNameChange?: (name: string) => void;
  onActiveChange?: (active: boolean) => void;
  availableStepTypes?: string[];
  onAIGenerate?: (name: string, steps: StepCreateRequest[]) => void;
}

export default function ProcessToolbar({
  processName,
  isActive,
  onSave,
  onExport,
  onImport,
  onAddStep,
  onZoomIn,
  onZoomOut,
  onFitView,
  onAutoLayout,
  isDirty,
  isValid,
  onNameChange,
  onActiveChange,
  availableStepTypes = [],
  onAIGenerate,
}: ProcessToolbarProps) {
  const navigate = useNavigate();
  const [aiDialogOpen, setAiDialogOpen] = useState(false);

  console.log("[ProcessToolbar] Rendered with availableStepTypes:", availableStepTypes);

  const handleOpenAIDialog = () => {
    console.log("[ProcessToolbar] Opening AI dialog");
    setAiDialogOpen(true);
  };

  const handleCloseAIDialog = () => {
    console.log("[ProcessToolbar] Closing AI dialog");
    setAiDialogOpen(false);
  };

  const handleAIGenerate = (name: string, steps: StepCreateRequest[]) => {
    console.log("[ProcessToolbar] AI generated process:", name, steps);
    if (onAIGenerate) {
      onAIGenerate(name, steps);
    }
    setAiDialogOpen(false);
  };

  return (
    <>
      <Box
        sx={{
          height: 72,
          bgcolor: adminTheme.colors.background,
          borderBottom: `1px solid ${adminTheme.colors.border}`,
          px: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        {/* Left side - Back button and Process info */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Tooltip title="Atgal į procesų sąrašą">
            <IconButton
              onClick={() => navigate("/process")}
              sx={{
                bgcolor: adminTheme.colors.backgroundAlt,
                "&:hover": { bgcolor: adminTheme.colors.border },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem />

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: adminTheme.borderRadius.sm,
                background: adminTheme.gradients.primary,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AccountTreeIcon sx={{ color: "white", fontSize: 24 }} />
            </Box>
            <Box>
              {onNameChange ? (
                <TextField
                  value={processName}
                  onChange={(e) => onNameChange(e.target.value)}
                  placeholder="Proceso pavadinimas"
                  variant="standard"
                  InputProps={{
                    sx: {
                      fontWeight: 700,
                      fontSize: "1.1rem",
                      "&:before": { borderColor: "transparent" },
                      "&:hover:not(.Mui-disabled):before": {
                        borderColor: adminTheme.colors.border,
                      },
                    },
                  }}
                />
              ) : (
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {processName || "Naujas procesas"}
                </Typography>
              )}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {onActiveChange && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Switch
                      size="small"
                      checked={isActive}
                      onChange={(e) => onActiveChange(e.target.checked)}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: adminTheme.colors.success,
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                          backgroundColor: adminTheme.colors.success,
                        },
                      }}
                    />
                  </Box>
                )}
                <Chip
                  label={isActive ? "Aktyvus" : "Neaktyvus"}
                  size="small"
                  sx={{
                    bgcolor: isActive
                      ? "rgba(16, 185, 129, 0.1)"
                      : adminTheme.colors.backgroundAlt,
                    color: isActive
                      ? adminTheme.colors.success
                      : adminTheme.colors.textMuted,
                    fontWeight: 600,
                    fontSize: "0.7rem",
                  }}
                />
                {isDirty && (
                  <Chip
                    label="Neišsaugota"
                    size="small"
                    sx={{
                      bgcolor: "rgba(245, 158, 11, 0.1)",
                      color: adminTheme.colors.warning,
                      fontWeight: 600,
                      fontSize: "0.7rem",
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Right side - View controls, AI, import/export, and save */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* AI Generator Button */}
          {onAIGenerate && availableStepTypes.length > 0 && (
            <>
              <Tooltip title="AI proceso generatorius">
                <Button
                  variant="outlined"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={handleOpenAIDialog}
                  sx={{
                    borderColor: "#8B5CF6",
                    color: "#8B5CF6",
                    fontWeight: 600,
                    "&:hover": {
                      borderColor: "#7C3AED",
                      bgcolor: "rgba(139, 92, 246, 0.08)",
                    },
                  }}
                >
                  AI
                </Button>
              </Tooltip>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            </>
          )}

          <Tooltip title="Automatinis išdėstymas">
            <IconButton
              onClick={onAutoLayout}
              sx={{
                bgcolor: adminTheme.colors.backgroundAlt,
                "&:hover": { bgcolor: adminTheme.colors.border },
              }}
            >
              <AutoFixHighIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Priartinti">
            <IconButton
              onClick={onZoomIn}
              sx={{
                bgcolor: adminTheme.colors.backgroundAlt,
                "&:hover": { bgcolor: adminTheme.colors.border },
              }}
            >
              <ZoomInIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Atitolinti">
            <IconButton
              onClick={onZoomOut}
              sx={{
                bgcolor: adminTheme.colors.backgroundAlt,
                "&:hover": { bgcolor: adminTheme.colors.border },
              }}
            >
              <ZoomOutIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Pritaikyti ekranui">
            <IconButton
              onClick={onFitView}
              sx={{
                bgcolor: adminTheme.colors.backgroundAlt,
                "&:hover": { bgcolor: adminTheme.colors.border },
              }}
            >
              <FitScreenIcon />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          <Tooltip title="Importuoti JSON">
            <IconButton
              onClick={onImport}
              sx={{
                bgcolor: adminTheme.colors.backgroundAlt,
                "&:hover": { bgcolor: adminTheme.colors.border },
              }}
            >
              <FileUploadIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Eksportuoti JSON">
            <IconButton
              onClick={onExport}
              sx={{
                bgcolor: adminTheme.colors.backgroundAlt,
                "&:hover": { bgcolor: adminTheme.colors.border },
              }}
            >
              <FileDownloadIcon />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          <Tooltip title={!isDirty ? "Nėra pakeitimų" : "Išsaugoti"}>
            <span>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={onSave}
                disabled={!isDirty || !isValid}
                sx={{
                  background: adminTheme.gradients.primary,
                  fontWeight: 600,
                  px: 3,
                  "&:hover": { background: adminTheme.gradients.primaryHover },
                  "&:disabled": {
                    background: adminTheme.colors.border,
                    color: adminTheme.colors.textMuted,
                  },
                }}
              >
                Išsaugoti
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* AI Generator Dialog */}
      <Dialog
        open={aiDialogOpen}
        onClose={handleCloseAIDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: adminTheme.borderRadius.lg,
            maxHeight: "90vh",
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <AIProcessGenerator
            onGenerate={handleAIGenerate}
            onCancel={handleCloseAIDialog}
            availableStepTypes={availableStepTypes}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
